import { AfterViewInit, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { PortalModuleTexture } from '@src/app/model/portal-module-texture';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { TranslateService } from '@ngx-translate/core';
import { Asset, id } from '@ess/jg-rule-executor';
import { ConfirmationModalComponent } from '@src/app/components/modals/confirmation-modal/confirmation-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { AssetService } from '@src/app/services/asset/asset.service';
import { first } from 'rxjs/operators';
import { AssetDialogComponent } from '@src/app/components/asset-dialog/asset-dialog.component';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { TypedFormModal } from '@src/app/components/utils/typed-form-modal';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';
import { SubjectType, TextureInput } from '@src/app/generated/globalTypes';

@Component({
  selector: 'app-textures',
  templateUrl: './textures.component.html',
  styleUrls: ['./textures.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false,
})
export class TexturesComponent implements OnInit, AfterViewInit, OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  private _textureFailMsg: string = 'textures.upload.failure';
  private _assetFailMsg: string = 'textures.upload.asset.fail';
  private _assetSuccessMsg: string = 'textures.upload.asset.success';
  private _skipFields: string[] = ['id', 'asset'];

  public displayedColumnsTextures: string[] = ['id', 'name', 'action'];
  public currentTextures: MatTableDataSource<PortalModuleTexture> = new MatTableDataSource<PortalModuleTexture>([]);

  readonly sort = viewChild(MatSort);

  constructor(
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private _apolloService: ApolloService,
    private _scopeService: ScopeService,
    private _assetService: AssetService
  ) {
  }

  public ngOnInit(): void {
    this.currentTextures.sort = this.sort();
    this._apolloService.getAllTextures$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((texturesAngPageData) => {
        if (texturesAngPageData.textures) {
          this.currentTextures.data = texturesAngPageData.textures;
        }
      });
  }

  public ngAfterViewInit(): void {
    this.currentTextures.sort = this.sort();
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
  }

  public openAddTextureDialog($event: any): void {
    this._openTextureJsonDialog($event, mat => this._apolloService.newTexture$(mat));
  }

  public deleteTexture($event: any, texture: PortalModuleTexture): void {
    $event.stopPropagation();
    this._openConfirmationModal(
      'textures.deletion.header',
      'textures.deletion.content',
      texture.name,
      this._apolloService.deleteTexture$(texture.id)
    );
  }

  public openEditTextureJsonDialog($event: any, texture: PortalModuleTexture): void {
    this._openTextureJsonDialog($event, mat => this._apolloService.updateTexture$(texture.id, mat), texture);
  }

  public openEditTextureFormDialog(texture: PortalModuleTexture): void {
    const dialogRef = TypedFormModal.openTypedFormModalDialog(
      this._dialog, new TypedFormModalDataObject(
        'texture',
        SubjectType.TexturesType,
        texture,
        texture.id,
        this._skipFields,
      )
    );
    TypedFormModal.useFormDialog(
      dialogRef,
      ((edited: TextureInput) =>
        this._apolloService.updateTexture$(texture.id, PortalModuleTexture.toJsonInput(edited))
      ).bind(this),
      this._translateService.instant(this._textureFailMsg),
      this._apolloService.getAllTexturesRefresh.bind(this._apolloService)
    );
  }

  public openAsset(asset: Asset) {
    this._dialog.open(AssetDialogComponent, { data: { asset } });
  }

  public uploadTexture(event: any, texture: PortalModuleTexture): void {
    const scope = this._scopeService.currentScope$.getValue()?.id;
    if (event.target.files.length > 0 && scope) {
      const assetMap = new Map().set('textureId', texture.id);
      this._assetService.uploadAsset$(event.target.files[0], assetMap)
        .pipe(first())
        .subscribe({
          next: (url) => {
            if (!url) {
              window.alert(this._translateService.instant(this._assetFailMsg));
            } else {
              this._apolloService.getAllTexturesRefresh();
              window.alert(this._translateService.instant(this._assetSuccessMsg));
            }
          },
          error: (_) => {
            window.alert(this._translateService.instant(this._assetFailMsg));
          },
        });
    }
  }

  private _openTextureJsonDialog(
    $event: any,
    updater: (bp: JSON) => Observable<boolean>,
    texture?: PortalModuleTexture
  ): void {
    $event.stopPropagation();
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'texture',
      texture || new PortalModuleTexture(id('ID'), 'NAME'),
      'textures.upload.header',
      'buttons.submit'
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      updater.bind(this),
      this._translateService.instant(this._textureFailMsg),
      this._apolloService.getAllTexturesRefresh.bind(this._apolloService)
    );
  }

  private _openConfirmationModal(header: string, content: string, texture: string, updater: Observable<boolean>): void {
    const dialogRef = this._dialog.open(ConfirmationModalComponent,
      {
        data: {
          headerText: header,
          contentText: content,
          values: { name: texture },
        },
      }
    );
    ConfirmationModalComponent.useConfirmDialog(
      dialogRef,
      updater,
      this._translateService.instant(this._textureFailMsg),
      this._apolloService.getAllTexturesRefresh.bind(this._apolloService)
    );
  }

  public sortChange(sortState: Sort): void {
  }
}
